import { useCallback, useEffect, useMemo, useRef } from "react";

import { useSubmitOnce } from "@/hooks/useSubmitOnce";
import { logError } from "@/lib/logError";
import { classifyMutationFailure } from "@/utils/mutationOutcome";

/** The two money-moving actions this hook guards. */
export type PayAction = "confirm" | "cancel";

/**
 * What a fresh read of the payment says about an action whose request ended
 * with no definite answer:
 * - `"applied"`: the server shows the action took effect;
 * - `"unchanged"`: the server shows it did not, so a retry is safe;
 * - `"undetermined"`: the read cannot tell either way.
 */
export type PayActionReread = "applied" | "unchanged" | "undetermined";

export type UsePayConfirmationSubmitArgs = {
  paymentId?: string;
  /**
   * The Confirm button is actionable per the server's answer: shown, the user
   * is not already a confirmer, and the payment is not yet CONFIRMED.
   */
  isConfirmEditable: boolean;
  /** Runs the confirm mutation for the resource type; rejects on failure. */
  confirm: (paymentId: string) => Promise<unknown>;
  /** Runs the cancel mutation for the resource type; rejects on failure. */
  cancel: (paymentId: string) => Promise<unknown>;
  onConfirmed: () => void;
  onCancelled: (paymentId: string) => void;
  onConfirmFailed: (error: unknown) => void;
  onCancelFailed: (error: unknown) => void;
  /**
   * Told each time the confirm guard latches (true) or re-arms (false), so
   * the host can disable Confirm while a click would be refused.
   */
  onConfirmLatchedChange?: (latched: boolean) => void;
  /**
   * Re-reads the payment past the cache after a confirm that ended with no
   * definite answer. Omitted, or rejecting, counts as `"undetermined"`.
   */
  rereadConfirm?: (paymentId: string) => Promise<PayActionReread>;
  /** As `rereadConfirm`, for a cancel. */
  rereadCancel?: (paymentId: string) => Promise<PayActionReread>;
  /**
   * The re-read could not tell whether the action took effect. The host tells
   * the customer to refresh and check; both actions stay held.
   */
  onOutcomeUnconfirmed?: (action: PayAction, error: unknown) => void;
  /**
   * Told when an unconfirmed outcome starts (true) or stops (false) holding
   * both actions, so the host can disable Confirm and Cancel.
   */
  onOutcomeHeldChange?: (held: boolean) => void;
};

/**
 * The Confirm and Cancel-payment handlers of `pay-confirmation-actions.tsx`,
 * each behind its own submit-once guard (`hooks/useSubmitOnce.ts`).
 *
 * The component stays mounted after either succeeds, so each success latches
 * its guard and a host signal re-arms it:
 * - Confirm re-arms when the host returns to an editable state — the Confirm
 *   button goes from not actionable back to actionable, or a different
 *   payment arrives. Until then the refetched requirements are what disable
 *   the button.
 * - Cancel re-arms when the cancel modal reopens (`rearmCancel`).
 *
 * A failure is classified first (`utils/mutationOutcome.ts`):
 * - definite (the server refused): release, so the customer can retry;
 * - unknown (no response, a timeout, any 5xx): the action may have been
 *   applied, so re-read the payment once. Applied runs the success path,
 *   unchanged releases and reports the failure, and a read that cannot tell
 *   holds both actions and reports an unconfirmed outcome. The hold survives
 *   the cancel re-arm and the confirm re-arm; only a different payment (or a
 *   reload, which remounts the host) clears it. A result that lands after the
 *   host has switched to a different payment is dropped silently: no hold and
 *   no unconfirmed report.
 *
 * `onConfirmLatchedChange` reports the confirm guard for rendering. The
 * mutation stops loading before the refetched requirements mark the user as a
 * confirmer, and a click in that window would hit the latched guard and do
 * nothing; the host disables Confirm while it is latched to close it.
 */
export function usePayConfirmationSubmit({
  paymentId,
  isConfirmEditable,
  confirm,
  cancel,
  onConfirmed,
  onCancelled,
  onConfirmFailed,
  onCancelFailed,
  onConfirmLatchedChange,
  rereadConfirm,
  rereadCancel,
  onOutcomeUnconfirmed,
  onOutcomeHeldChange,
}: UsePayConfirmationSubmitArgs) {
  const confirmGuard = useSubmitOnce();
  const cancelGuard = useSubmitOnce();

  // The host passes these as inline arrows, so their identity changes on
  // every render. Reading them through a ref keeps the returned handlers
  // stable; the ref is synced after each commit, before any click can reach
  // a handler.
  const callbacksRef = useRef({
    confirm,
    cancel,
    onConfirmed,
    onCancelled,
    onConfirmFailed,
    onCancelFailed,
    onConfirmLatchedChange,
    rereadConfirm,
    rereadCancel,
    onOutcomeUnconfirmed,
    onOutcomeHeldChange,
  });
  useEffect(() => {
    callbacksRef.current = {
      confirm,
      cancel,
      onConfirmed,
      onCancelled,
      onConfirmFailed,
      onCancelFailed,
      onConfirmLatchedChange,
      rereadConfirm,
      rereadCancel,
      onOutcomeUnconfirmed,
      onOutcomeHeldChange,
    };
  });

  // The guard is a ref, so it cannot drive a render on its own. This follows
  // it (set on every successful begin(), cleared on every release()) and tells
  // the host only when the value actually changes.
  const confirmLatchedRef = useRef(false);
  const setConfirmLatched = useCallback((latched: boolean) => {
    if (confirmLatchedRef.current === latched) return;
    confirmLatchedRef.current = latched;
    callbacksRef.current.onConfirmLatchedChange?.(latched);
  }, []);

  // The action whose outcome is unconfirmed, if any. It blocks both handlers
  // and both re-arms until a different payment arrives.
  const heldActionRef = useRef<PayAction | null>(null);
  const setHeldAction = useCallback((action: PayAction | null) => {
    const wasHeld = heldActionRef.current !== null;
    heldActionRef.current = action;
    if (wasHeld !== (action !== null)) {
      callbacksRef.current.onOutcomeHeldChange?.(action !== null);
    }
  }, []);

  // The payment the host shows now, so a re-read that lands after a switch
  // does not hold the next payment.
  const currentPaymentIdRef = useRef(paymentId);

  const previousHostRef = useRef({ paymentId, isConfirmEditable });
  useEffect(() => {
    const previous = previousHostRef.current;
    previousHostRef.current = { paymentId, isConfirmEditable };
    currentPaymentIdRef.current = paymentId;
    if (previous.paymentId !== paymentId) {
      if (heldActionRef.current === "cancel") cancelGuard.release();
      setHeldAction(null);
      confirmGuard.release();
      setConfirmLatched(false);
      return;
    }
    if (
      !previous.isConfirmEditable &&
      isConfirmEditable &&
      heldActionRef.current === null
    ) {
      confirmGuard.release();
      setConfirmLatched(false);
    }
  }, [
    paymentId,
    isConfirmEditable,
    confirmGuard,
    cancelGuard,
    setConfirmLatched,
    setHeldAction,
  ]);

  return useMemo(() => {
    const reread = async (
      action: PayAction,
      id: string
    ): Promise<PayActionReread> => {
      const read =
        action === "confirm"
          ? callbacksRef.current.rereadConfirm
          : callbacksRef.current.rereadCancel;
      if (!read) return "undetermined";
      try {
        return await read(id);
      } catch (rereadError: unknown) {
        // Reported to the customer as an unconfirmed outcome by the caller.
        logError(
          "Payment re-read after an unknown outcome failed",
          rereadError,
          {
            flow: "pay-confirmation",
            step: action,
          }
        );
        return "undetermined";
      }
    };

    // A result for a payment the host no longer shows is dropped: holding or
    // warning would point the customer at the payment now on screen.
    const holdOutcome = (action: PayAction, id: string, error: unknown) => {
      if (currentPaymentIdRef.current !== id) return;
      setHeldAction(action);
      callbacksRef.current.onOutcomeUnconfirmed?.(action, error);
    };

    const handleConfirmPayment = async () => {
      if (!paymentId) return;
      if (heldActionRef.current !== null) return;
      if (!confirmGuard.begin()) return;
      setConfirmLatched(true);
      try {
        await callbacksRef.current.confirm(paymentId);
        callbacksRef.current.onConfirmed();
      } catch (error: unknown) {
        const outcome =
          classifyMutationFailure(error) === "unknown"
            ? await reread("confirm", paymentId)
            : "unchanged";
        if (outcome === "applied") {
          callbacksRef.current.onConfirmed();
        } else if (outcome === "unchanged") {
          confirmGuard.release();
          setConfirmLatched(false);
          callbacksRef.current.onConfirmFailed(error);
        } else {
          holdOutcome("confirm", paymentId, error);
        }
      }
    };

    const handleCancelPayment = async () => {
      if (!paymentId) return;
      if (heldActionRef.current !== null) return;
      if (!cancelGuard.begin()) return;
      try {
        await callbacksRef.current.cancel(paymentId);
        callbacksRef.current.onCancelled(paymentId);
      } catch (error: unknown) {
        const outcome =
          classifyMutationFailure(error) === "unknown"
            ? await reread("cancel", paymentId)
            : "unchanged";
        if (outcome === "applied") {
          callbacksRef.current.onCancelled(paymentId);
        } else if (outcome === "unchanged") {
          cancelGuard.release();
          callbacksRef.current.onCancelFailed(error);
        } else {
          holdOutcome("cancel", paymentId, error);
        }
      }
    };

    // Opening the cancel modal re-arms a cancel latched by a success, never
    // one held by an unconfirmed outcome.
    const rearmCancel = () => {
      if (heldActionRef.current !== null) return;
      cancelGuard.release();
    };

    return {
      handleConfirmPayment,
      handleCancelPayment,
      rearmCancel,
    };
  }, [paymentId, confirmGuard, cancelGuard, setConfirmLatched, setHeldAction]);
}
